import React, { useState } from 'react';
import { Button, Flex, Popconfirm, Radio, Tag, Tooltip, Typography } from 'antd';
import { ExportOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ApiError, fetchPatentStatus, startPatentCheck, type PatentStatus } from '../../shared/api/client';
import HintLabel from '../../shared/ui/HintLabel';
import {
  formatCheckedAt,
  riskTone,
  snapshotFrom,
  type PatentCheckSnapshot,
  type PatentVerdict,
} from '../../shared/lib/patentCheck';
import { isAsin } from '../../shared/lib/asin';
import { usePlatformOrigin } from '../../shared/lib/platformOrigin';
import { buildPlatformPatentReportLink } from '../../shared/lib/platformShareLink';

interface PatentCheckProps {
  asin: string;
  value: PatentCheckSnapshot | null;
  onChange: (next: PatentCheckSnapshot | null) => void;
}

type Started = { reportUrl: string; deduplicated: boolean };

/**
 * Проверка патентов (WS 15587410, п.4): по кнопке — что IP Shield знает об ASIN (риск,
 * находки, отчёт в новой вкладке); не проверяли — «Запустить проверку» с подтверждением,
 * она платная. Вывод «чисто / не чисто» делает человек и он сохраняется в расчёте вместе
 * со снимком риска.
 */
const PatentCheck: React.FC<PatentCheckProps> = ({ asin, value, onChange }) => {
  const { t, i18n } = useTranslation();
  const asinOk = isAsin(asin);
  const platformOrigin = usePlatformOrigin();
  const [status, setStatus] = useState<PatentStatus | null>(null);
  const [started, setStarted] = useState<Started | null>(null);
  const [loading, setLoading] = useState<'status' | 'start' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const errorText = (e: unknown) => {
    const code = e instanceof ApiError ? e.status : undefined;
    if (code === 401) return t('idea_calc.patent.error_platform_only');
    if (code === 403) return t('idea_calc.patent.error_rejected');
    if (code === 429) return t('idea_calc.patent.error_queue');
    if (code === 503) return t('idea_calc.patent.error_not_configured');
    return t('idea_calc.patent.error_unavailable');
  };

  const load = async () => {
    setLoading('status');
    setError(null);
    try {
      const s = await fetchPatentStatus(asin);
      setStatus(s);
      if (s.checked) {
        setStarted(null);
        onChange(snapshotFrom(s, value?.verdict ?? null, value));
      }
    } catch (e: unknown) {
      setError(errorText(e));
    } finally {
      setLoading(null);
    }
  };

  const start = async () => {
    setLoading('start');
    setError(null);
    try {
      const res = await startPatentCheck(asin);
      setStarted({ reportUrl: res.report_url, deduplicated: res.deduplicated });
    } catch (e: unknown) {
      setError(errorText(e));
    } finally {
      setLoading(null);
    }
  };

  const setVerdict = (verdict: PatentVerdict | null) => {
    const next = snapshotFrom(status, verdict, value);
    onChange(next.verdict || next.risk_level || next.checked_at ? next : null);
  };

  // Что показывать: свежий статус из IP Shield, иначе — снимок из сохранённого расчёта.
  const shown = status?.checked ? snapshotFrom(status, value?.verdict ?? null, value) : value;
  const reportUrl = started?.reportUrl ?? status?.report_url ?? null;
  const findings = shown?.findings;
  const checkedAt = formatCheckedAt(shown?.checked_at, i18n.language);

  return (
    <div data-testid="patent-check">
      <Flex align="center" gap="small" wrap>
        <Typography.Text type="secondary">
          <HintLabel hint={t('idea_calc.patent.tips.label')}>{t('idea_calc.patent.label')}</HintLabel>
        </Typography.Text>

        {shown?.risk_level && (
          <Tooltip title={shown.risk_source === 'manual' ? t('idea_calc.patent.tips.manual') : t('idea_calc.patent.tips.auto')}>
            <Tag color={riskTone(shown.risk_level)} style={{ marginInlineEnd: 0 }}>
              {t('idea_calc.patent.risk', { level: t(`idea_calc.patent.level.${shown.risk_level.toLowerCase()}`, { defaultValue: shown.risk_level }) })}
              {shown.risk_source === 'manual' ? ` · ${t('idea_calc.patent.manual')}` : ''}
            </Tag>
          </Tooltip>
        )}
        {findings && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('idea_calc.patent.findings', {
              design: findings.design_patents,
              utility: findings.utility_patents,
              tm: findings.trademarks,
            })}
            {checkedAt ? ` · ${t('idea_calc.patent.checked_at', { date: checkedAt })}` : ''}
          </Typography.Text>
        )}

        {status && !status.checked && !started && (
          <>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('idea_calc.patent.not_checked')}</Typography.Text>
            <Popconfirm
              title={t('idea_calc.patent.start_confirm_title')}
              description={t('idea_calc.patent.start_confirm')}
              okText={t('idea_calc.patent.start_ok')}
              cancelText={t('idea_calc.patent.cancel')}
              onConfirm={start}
            >
              <Button size="small" type="primary" loading={loading === 'start'}>{t('idea_calc.patent.start_button')}</Button>
            </Popconfirm>
          </>
        )}
        {started && (
          <Tag color="processing" style={{ marginInlineEnd: 0 }}>
            {started.deduplicated ? t('idea_calc.patent.already_queued') : t('idea_calc.patent.started')}
          </Tag>
        )}

        <Tooltip title={asinOk ? undefined : t('idea_calc.patent.need_asin')}>
          <Button
            size="small"
            icon={status || value ? <ReloadOutlined /> : <SafetyCertificateOutlined />}
            disabled={!asinOk}
            loading={loading === 'status'}
            onClick={load}
          >
            {status || value ? t('idea_calc.patent.refresh_button') : t('idea_calc.patent.check_button')}
          </Button>
        </Tooltip>
        {reportUrl && (
          // На платформе отчёт открывается в её разделе Patent Checker; ссылка сервиса вела
          // на отдельный сайт модуля со своим входом (QA, WS 15587410). В standalone
          // платформы нет — остаётся ссылка сервиса.
          <a
            href={platformOrigin && asinOk ? buildPlatformPatentReportLink(platformOrigin, asin) : reportUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('idea_calc.patent.report_link')} <ExportOutlined />
          </a>
        )}
      </Flex>

      {(shown?.risk_level || shown?.verdict || status?.checked) && (
        <Flex align="center" gap="small" wrap style={{ marginTop: 6 }}>
          <Typography.Text>
            <HintLabel hint={t('idea_calc.patent.tips.verdict')}>{t('idea_calc.patent.verdict_label')}</HintLabel>
          </Typography.Text>
          <Radio.Group size="small" optionType="button" buttonStyle="solid" value={value?.verdict ?? null}>
            {(['clean', 'not_clean'] as const).map((v) => (
              <Radio.Button
                key={v}
                value={v}
                // Повторный клик снимает вывод: «не решил» — тоже законное состояние.
                onClick={() => setVerdict(value?.verdict === v ? null : v)}
              >
                {t(`idea_calc.patent.verdict.${v}`)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Flex>
      )}

      {error && <Typography.Text type="danger" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{error}</Typography.Text>}
    </div>
  );
};

export default PatentCheck;
